import { Box, Button, DialogActions, Stack, Typography } from '@mui/material';
import { ErrorBanner } from 'shared/ui/error-banner';
import { LegendList, LegendItem } from 'shared/ui/legend-list';
import { LoadingBanner } from 'shared/ui/loading-banner';
import { Dialog } from 'shared/ui/dialog';
import { useBulkCreate } from 'features/wcc-bulk-create/lib';
import { useActionCreators, useStateSelector } from 'shared/store';
import { wccBulkCreateActions } from './slice';

type Props = {
  onSuccess: () => void;
};

export const BulkCreateProgressDialog = ({ onSuccess }: Props) => {
  const open = useStateSelector(state => state.wcc.bulkCreate.dialogOpen);
  const idList = useStateSelector(state => state.wcc.bulkCreate.checkedItems);
  const { closeDialog, uncheckAll } = useActionCreators(wccBulkCreateActions);

  const { create, report, loading, error, reset } = useBulkCreate();

  const handleClose = () => {
    reset();
    closeDialog();
  }

  const handleCreate = () => create(idList).then(() => {
    uncheckAll();
    onSuccess();
  })

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='lg'
    >
      <Dialog.Title>
        Создание актов
      </Dialog.Title>

      <Dialog.Content>
        {loading && <LoadingBanner />}

        {report
          ?
          <Box>
            <LegendList>
              <LegendItem 
                title="Успешно создано актов:" 
                value={report.createdNumber} 
              />
              <LegendItem 
                title="Не удалось создать:" 
                value={report.failedNumber}
              />
            </LegendList>
            {report.failedNumber > 0 && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Причины ошибок:
                </Typography>
                <Box component="pre" sx={{ 
                  whiteSpace: 'pre-wrap', 
                  fontSize: '0.875rem',
                  m: 0,
                  color: 'error.main'
                }}>
                  {report.errorDetails || 'Отсутствуют данные главного бухгалтера'}
                </Box>
              </Box>
            )}
          </Box>
          :
          error
            ? (
              <Box>
                <ErrorBanner errorMessage={error} />
                {error.includes('не заполнен бухгалтер') && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Рекомендации:
                    </Typography>
                    <Typography variant="body2">
                      1. Перейдите в раздел "Юридические лица"<br />
                      2. Найдите нужные юрлица по ID из сообщения об ошибке<br />
                      3. Заполните данные главного бухгалтера<br />
                      4. Повторите попытку создания актов
                    </Typography>
                  </Box>
                )}
              </Box>
            )
            :
            <Box>
              <Box sx={{ mb: 2 }}>
                {`Создать акты для ${idList.length} счетов?`}
              </Box>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Обратите внимание:
                </Typography>
                <Typography variant="body2">
                  • Для создания актов требуется заполненный главный бухгалтер<br />
                  • Счета без бухгалтера будут пропущены<br />
                  • После исправления данных можно повторить операцию
                </Typography>
              </Box>
            </Box>
        }
      </Dialog.Content>

      <DialogActions sx={{ flex: 1, flexDirection: 'row' }}>
        <Stack direction="row" justifyContent="flex-start" spacing={1}>
          {!error && !report && (
            <Button
              variant="contained"
              disabled={loading || !!report}
              onClick={handleCreate}
            >
              Создать
            </Button>
          )}
          
          <Button
            variant={error || report ? "contained" : "outlined"}
            onClick={handleClose}
          >
            {error || report ? 'Закрыть' : 'Отмена'}
          </Button>
        </Stack>
      </DialogActions>

    </Dialog>
  );
};